import { ReactNode, useState } from 'react';

import { useDrawdownRepaymentSchedule } from '@api/lendingAPI/lapiHooks';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconInfoCircleFilled } from '@iwoca/icons';
import { Input, Label } from '@iwoca/orion';
import { Text } from '@iwoca/orion-ui';
import { Skeleton } from '@ui';
import { addDays, addWeeks, addMonths, format } from 'date-fns';
import { FormattedMessage } from 'react-intl';

import { MoneyWithDecimals } from '../../../../../../ui/moneyWithDecimals';

const PRESET_DATES = [
    {
        key: '4-weeks',
        label: (
            <FormattedMessage
                id="_.choose_amount.early_repayment.four_weeks"
                defaultMessage="4 weeks"
            />
        ),
        getDate: () => addWeeks(new Date(), 4)
    },
    {
        key: '6-months',
        label: (
            <FormattedMessage
                id="_.choose_amount.early_repayment.six_months"
                defaultMessage="6 months"
            />
        ),
        getDate: () => addMonths(new Date(), 6)
    },
    {
        key: '9-months',
        label: (
            <FormattedMessage
                id="_.choose_amount.early_repayment.nine_months"
                defaultMessage="9 months"
            />
        ),
        getDate: () => addMonths(new Date(), 9)
    }
];

const SummaryRow = ({
    label,
    value,
    isLoading
}: {
    label: ReactNode;
    value: ReactNode;
    isLoading: boolean;
}) => (
    <div className="flex justify-between py-s">
        <Text className="font-reg">{label}</Text>
        <Text className="font-bold">
            {isLoading ? (
                <Skeleton
                    data-testid="summary-row-skeleton"
                    className="h-[20px] w-[80px]"
                />
            ) : (
                value
            )}
        </Text>
    </div>
);

export const RepaymentCalculator = ({
    drawdownAmount,
    selectedOffer
}: {
    drawdownAmount: number;
    selectedOffer: TFormalOffer;
}) => {
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [activePreset, setActivePreset] = useState<string | null>(null);

    const offerId = selectedOffer.offer_id!;
    const minDate = format(new Date(), 'yyyy-MM-dd');
    const maxDate = format(
        addDays(new Date(), selectedOffer.duration!.amount - 1),
        'yyyy-MM-dd'
    );

    const { data, isLoading, isError } = useDrawdownRepaymentSchedule({
        offerId,
        drawdownAmount,
        earlyFinalRepaymentDate: selectedDate,
        enabled: Boolean(selectedDate)
    });

    const scheduleData = data?.summary;

    const handleDateChange = (selectedDate: string, presetLabel?: string) => {
        if (Date.parse(selectedDate) >= Date.parse(minDate)) {
            setSelectedDate(selectedDate);
            tracking.ampli.earlyRepaymentCalculatorUsed({
                early_repayment_date: selectedDate,
                preset_button: presetLabel ?? ''
            });
        }
    };

    return (
        <div>
            <Text className="mb-s font-bold">
                <FormattedMessage
                    id="_.inline.early_repayment.titleLineTwo"
                    defaultMessage="Select a repayment date to see how it could look"
                />
            </Text>

            <div className="flex flex-wrap items-center justify-between gap-m">
                <Label
                    className="shrink-0 text-s font-reg"
                    htmlFor="repayment-date-picker"
                >
                    <FormattedMessage
                        id="_.choose_amount.early_repayment.repay"
                        defaultMessage="What if I repay early in full on:"
                    />
                </Label>

                <Input
                    id="repayment-date-picker"
                    className="p-s font-bold text-structure-40"
                    type="date"
                    value={selectedDate ?? ''}
                    min={minDate}
                    max={maxDate}
                    onChange={(e) => {
                        setActivePreset(null);
                        handleDateChange(e.target.value);
                    }}
                />
            </div>

            <div className="mt-m flex items-center justify-between gap-s">
                <Text className="shrink-0 text-s text-structure-40">
                    <FormattedMessage
                        id="_.repayment_calculator.or_in"
                        defaultMessage="or in..."
                    />
                </Text>
                <div className="flex max-w-[464px] grow gap-s">
                    {PRESET_DATES.map((preset) => (
                        <button
                            key={preset.key}
                            className={`grow rounded-s border p-s font-bold text-primary-40 ${
                                activePreset === preset.key
                                    ? 'border-primary-40'
                                    : 'border-structure-80'
                            }`}
                            onClick={() => {
                                setActivePreset(preset.key);
                                handleDateChange(
                                    format(preset.getDate(), 'yyyy-MM-dd'),
                                    preset.key
                                );
                            }}
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>
            </div>

            {selectedDate && isError && (
                <Text className="pt-l text-center text-s font-med text-core-40">
                    <FormattedMessage
                        id="_.compare_offers_table.error"
                        defaultMessage="Something went wrong. Please try again later."
                    />
                </Text>
            )}

            {selectedDate && !isError && (
                <div className="mt-2xl">
                    <Text className="font-bold">
                        <FormattedMessage
                            id="_.repayment_calculator.breakdown_title"
                            defaultMessage="If you repay in full on {date}..."
                            values={{
                                date: format(new Date(selectedDate), 'dd/MM/yy')
                            }}
                        />
                    </Text>

                    <div className="mt-m">
                        <SummaryRow
                            label={
                                <FormattedMessage
                                    id="_.repayment_calculator.total_interest"
                                    defaultMessage="Total interest"
                                />
                            }
                            value={
                                <MoneyWithDecimals
                                    value={scheduleData?.total_interest}
                                />
                            }
                            isLoading={isLoading}
                        />
                        <SummaryRow
                            label={
                                <FormattedMessage
                                    id="_.repayment_calculator.early_repayment_fees"
                                    defaultMessage="Early repayment fees"
                                />
                            }
                            value={
                                <FormattedMessage
                                    id="_.repayment_calculator.always_zero_fees"
                                    defaultMessage="always £0.00"
                                />
                            }
                            isLoading={false}
                        />
                        <SummaryRow
                            label={
                                <FormattedMessage
                                    id="_.repayment_calculator.total_repayment"
                                    defaultMessage="Total repayment"
                                />
                            }
                            value={
                                <MoneyWithDecimals
                                    value={scheduleData?.total_payable}
                                />
                            }
                            isLoading={isLoading}
                        />
                    </div>

                    <div className="mt-l flex items-center justify-end gap-s text-right font-reg text-success-drk">
                        <FormattedMessage
                            id="_.repayment_calculator.savings"
                            defaultMessage="this saves you {amount}"
                            values={{
                                amount: isLoading ? (
                                    <Skeleton className="h-[20px] w-[80px]" />
                                ) : (
                                    <span className="font-bold">
                                        <MoneyWithDecimals
                                            value={
                                                scheduleData?.amount_saved ?? 0
                                            }
                                        />
                                    </span>
                                )
                            }}
                        />
                    </div>

                    <div className="mt-l flex items-start gap-m rounded-m bg-primary-97 p-m">
                        <IconInfoCircleFilled
                            className="shrink-0 text-secondary-40"
                            fontSize="20px"
                        />
                        <Text className="text-m font-med text-secondary-40">
                            <FormattedMessage
                                id="_.choose_amount.early_repayment.info"
                                defaultMessage="Your repayment schedule will always be for {months} months but you can repay early in your dashboard at any time."
                                values={{
                                    months: getDurationInMonths(
                                        selectedOffer.duration!
                                    )
                                }}
                            />
                        </Text>
                    </div>
                </div>
            )}
        </div>
    );
};
